/**
 * Utility functions for file operations
 */

/**
 * Check if a URL refers to a PDF file
 * @param url - The URL to check
 * @returns boolean - True if the URL refers to a PDF file
 */
export const isPdfUrl = (url: string): boolean => {
  if (!url) return false

  // Check file extension
  if (url.toLowerCase().endsWith('.pdf')) return true

  // Check for PDF MIME type in URL query parameters
  if (url.toLowerCase().includes('application/pdf')) return true

  // Check for content-type=pdf in URL
  if (url.toLowerCase().includes('content-type=pdf')) return true

  return false
}

/**
 * Extract a filename from a URL
 * @param url - The URL to extract filename from
 * @returns string - The extracted filename or a default name
 */
export const extractFilenameFromUrl = (url: string): string => {
  if (!url) return 'Document'

  try {
    // Try to get the filename from the URL path
    const pathParts = new URL(url).pathname.split('/')
    const fileName = pathParts[pathParts.length - 1]

    // If we got a filename with an extension, return it
    if (fileName && fileName.includes('.')) {
      const withoutQuery = fileName.split('?')[0]
      try {
        return decodeURIComponent(withoutQuery)
      } catch {
        return withoutQuery
      }
    }

    // Otherwise, try to extract from the last part of the path
    if (!fileName) return 'Document'
    try {
      return decodeURIComponent(fileName)
    } catch {
      return fileName
    }
  } catch (error) {
    // If URL parsing fails, try a simpler approach
    const parts = url.split('/')
    const lastPart = parts[parts.length - 1]?.split('?')[0] || 'Document'
    try {
      return decodeURIComponent(lastPart)
    } catch {
      return lastPart
    }
  }
}

/**
 * Split a filename into base name and extension.
 * Returns { base, ext } where ext is null if no extension is found.
 */
export const splitFileName = (name: string): { base: string; ext: string | null } => {
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex > 0 && dotIndex < name.length - 1) {
    return { base: name.slice(0, dotIndex), ext: name.slice(dotIndex + 1) }
  }
  return { base: name, ext: null }
}

/**
 * Determine if a URL is valid
 * @param url - The URL to validate
 * @returns boolean - True if the URL is valid
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

/**
 * Get the MIME type for a file, falling back to extension-based detection
 */
export const getMimeType = (file: File): string => {
  if (file.type) {
    return file.type
  }

  switch (splitFileName(file.name).ext?.toLowerCase()) {
    case 'gpkg':
      return 'application/geopackage+sqlite3'
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'bmp':
      return 'image/bmp'
    default:
      return 'application/octet-stream'
  }
}
